import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { adminApi } from '../lib/api';
import { User } from '../types';
import { Shield, Plus, Edit2, Check, X, Key, Copy, Tag, UserX, UserCheck } from 'lucide-react';
import { SlackSettingsPanel } from '../components/SlackSettingsPanel';
import { RBACPermissionsMatrix } from '../components/RBACPermissionsMatrix';
import { VERTICALS } from '../constants/verticals';
import { useVerticals, invalidateVerticalsCache } from '../hooks/useVerticals';

export function AdminPage() {
  const verticalOptions = useVerticals();
  const [users, setUsers] = useState<User[]>([]);
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'creative',
    upload_limit_monthly: 100,
    assigned_verticals: [] as string[],
    additional_roles: [] as string[],
    pml_code: '',
  });
  const [verticalSearch, setVerticalSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [error, setError] = useState('');

  // Password reset state
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null);
  const [adminPassword, setAdminPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

  // Rejection reason state
  const [rejectingUserId, setRejectingUserId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Vertical management state
  const [managedVerticals, setManagedVerticals] = useState<any[]>([]);
  const [newVerticalName, setNewVerticalName] = useState('');
  const [verticalLoading, setVerticalLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, pendingRes, statsRes] = await Promise.all([
        adminApi.getUsers(),
        adminApi.getPendingUsers(),
        adminApi.getStats(),
      ]);
      setUsers(usersRes.data.data || []);
      setPendingUsers(pendingRes.data.data || []);
      setStats(statsRes.data.data || null);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
    } finally {
      setLoading(false);
    }
    // Also fetch managed verticals
    try {
      const vRes = await adminApi.getVerticals();
      setManagedVerticals(vRes.data.data || []);
    } catch (_) {}
  };

  const handleAddVertical = async () => {
    if (!newVerticalName.trim()) return;
    setVerticalLoading(true);
    try {
      await adminApi.createVertical({ name: newVerticalName.trim() });
      setNewVerticalName('');
      const vRes = await adminApi.getVerticals();
      setManagedVerticals(vRes.data.data || []);
      invalidateVerticalsCache();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create vertical');
    } finally {
      setVerticalLoading(false);
    }
  };

  const handleDeleteVertical = async (id: string) => {
    try {
      await adminApi.deleteVertical(id);
      const vRes = await adminApi.getVerticals();
      setManagedVerticals(vRes.data.data || []);
      invalidateVerticalsCache();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete vertical');
    }
  };

  const handleAddUser = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      setError('All fields are required');
      return;
    }

    try {
      await adminApi.createUser(formData);
      setFormData({ name: '', email: '', password: '', role: 'creative', upload_limit_monthly: 100, assigned_verticals: [], additional_roles: [], pml_code: '' });
      setShowAddForm(false);
      setError('');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleToggleActive = async (user: User) => {
    const action = user.is_active ? 'deactivate' : 'reactivate';
    if (!window.confirm(`${action === 'deactivate' ? 'Deactivate' : 'Reactivate'} ${user.name}? ${action === 'deactivate' ? 'They will no longer be able to log in, but all their files remain intact.' : 'They will regain full access.'}`)) return;
    try {
      await adminApi.updateUser(user.id, { is_active: !user.is_active });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to ${action} user`);
    }
  };

  const handleUpdateUser = async (id: string) => {
    try {
      await adminApi.updateUser(id, {
        name: formData.name,
        role: formData.role as any,
        upload_limit_monthly: formData.upload_limit_monthly,
        assigned_verticals: formData.assigned_verticals,
        additional_roles: formData.additional_roles,
        pml_code: formData.pml_code || null,
      } as any);
      setEditingId(null);
      setFormData({ name: '', email: '', password: '', role: 'creative', upload_limit_monthly: 100, assigned_verticals: [], additional_roles: [], pml_code: '' });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const startEdit = (user: User) => {
    setEditingId(user.id);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      upload_limit_monthly: user.upload_limit_monthly,
      assigned_verticals: (user as any).assigned_verticals || [],
      additional_roles: (user as any).additional_roles || [],
      pml_code: (user as any).pml_code || '',
    });
  };

  const handleResetPassword = async (userId: string) => {
    if (!adminPassword || !newPassword) {
      setError('Please enter both your admin password and new user password');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    try {
      const response = await adminApi.resetUserPassword(userId, {
        admin_password: adminPassword,
        new_password: newPassword
      });

      setGeneratedPassword(response.data.data.new_password);
      setAdminPassword('');
      setNewPassword('');
      setError('');

      // Log activity
      console.log('Password reset successful for user:', userId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  const closeResetModal = () => {
    setResetPasswordUserId(null);
    setAdminPassword('');
    setNewPassword('');
    setGeneratedPassword(null);
    setError('');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Password copied to clipboard!');
  };

  const handleApproveUser = async (userId: string) => {
    if (!window.confirm('Approve this user registration?')) return;

    try {
      await adminApi.approveUser(userId);
      alert('User approved successfully!');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve user');
      alert(err.response?.data?.error || 'Failed to approve user');
    }
  };

  const handleRejectUser = async (userId: string) => {
    try {
      await adminApi.rejectUser(userId, { reason: rejectionReason });
      alert('User registration rejected');
      setRejectingUserId(null);
      setRejectionReason('');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reject user');
      alert(err.response?.data?.error || 'Failed to reject user');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Admin Panel">
        <div className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">Loading admin panel...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Admin Panel"
      actions={
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          <Plus size={16} className="mr-2" />
          Add User
        </Button>
      }
    >
      <div className="space-y-6">
        {/* System Stats */}
        {stats && (
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total Users</CardDescription>
                <CardTitle className="text-3xl">{stats.users?.total || 0}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total Editors</CardDescription>
                <CardTitle className="text-3xl">{stats.editors?.total || 0}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total Files</CardDescription>
                <CardTitle className="text-3xl">{stats.storage?.total_files || 0}</CardTitle>
              </CardHeader>
            </Card>
          </div>
        )}

        {/* Vertical Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Tag size={18} /> Manage Verticals</CardTitle>
            <CardDescription>Add, view, or deactivate verticals. Changes apply system-wide.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-4">
              <Input
                placeholder="New vertical name..."
                value={newVerticalName}
                onChange={e => setNewVerticalName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddVertical(); }}
              />
              <Button onClick={handleAddVertical} disabled={verticalLoading || !newVerticalName.trim()}>
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Show hardcoded verticals */}
              {VERTICALS.map(v => (
                <span key={v} className="inline-flex items-center px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm">
                  {v}
                </span>
              ))}
              {/* Show dynamic verticals */}
              {managedVerticals.filter(v => v.is_active && !VERTICALS.includes(v.name)).map(v => (
                <span key={v.id} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm">
                  {v.name}
                  <button onClick={() => handleDeleteVertical(v.id)} className="ml-1 hover:text-red-500" title="Deactivate">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            {managedVerticals.filter(v => !v.is_active).length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs text-muted-foreground mb-1">Deactivated:</p>
                <div className="flex flex-wrap gap-1">
                  {managedVerticals.filter(v => !v.is_active).map(v => (
                    <span key={v.id} className="text-xs text-gray-400 line-through">{v.name}</span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Slack Integration */}
        <SlackSettingsPanel />

        {/* RBAC Permission Matrix */}
        <RBACPermissionsMatrix />

        {/* Add User Form */}
        {showAddForm && (
          <Card>
            <CardHeader>
              <CardTitle>Add New User</CardTitle>
              <CardDescription>Create a new user account</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {error && <p className="text-sm text-destructive">{error}</p>}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Name</label>
                    <Input
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email</label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Password</label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Primary Role</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    >
                      <option value="creative">Creative</option>
                      <option value="buyer">Buyer</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <label className="text-sm font-medium">Additional Roles (optional)</label>
                    <div className="flex flex-wrap gap-2">
                      {['team_lead', 'assistant_team_lead', 'vertical_head', 'ceo', 'head_media_buying', 'creative_head', 'quality_check', 'variation_studio'].map(r => (
                        <label key={r} className="flex items-center gap-1.5 px-2 py-1 rounded border border-input hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.additional_roles.includes(r)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ ...formData, additional_roles: [...formData.additional_roles, r] });
                              } else {
                                setFormData({ ...formData, additional_roles: formData.additional_roles.filter(x => x !== r) });
                              }
                            }}
                            className="rounded border-gray-300 text-blue-600"
                          />
                          <span className="text-sm capitalize">{r.replaceAll('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                    {formData.additional_roles.length > 0 && (
                      <p className="text-xs text-blue-600">{formData.additional_roles.length} additional role(s) selected</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">PML Code</label>
                    <Input
                      placeholder="e.g. PML80, PMLINT07 (optional)"
                      value={formData.pml_code}
                      onChange={(e) => setFormData({ ...formData, pml_code: e.target.value.replace(/\s+/g, '').toUpperCase() })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Internal employee identifier used in campaign / ad naming. Leave blank if not assigned.
                    </p>
                  </div>
                  {(['team_lead', 'assistant_team_lead', 'creative'].includes(formData.role) ||
                    formData.additional_roles?.some(r => ['vertical_head', 'team_lead', 'assistant_team_lead'].includes(r))) && (
                    <div className="space-y-2 col-span-2">
                      <label className="text-sm font-medium">Assigned Verticals</label>
                      <Input
                        placeholder="Search verticals..."
                        value={verticalSearch}
                        onChange={(e) => setVerticalSearch(e.target.value)}
                        className="mb-1"
                      />
                      <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                        {verticalOptions.filter(v => !verticalSearch || v.toLowerCase().includes(verticalSearch.toLowerCase())).map(v => (
                          <label key={v} className="flex items-center gap-1.5 px-2 py-1 rounded border border-input hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.assigned_verticals.includes(v)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({ ...formData, assigned_verticals: [...formData.assigned_verticals, v] });
                                } else {
                                  setFormData({ ...formData, assigned_verticals: formData.assigned_verticals.filter(x => x !== v) });
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600"
                            />
                            <span className="text-sm">{v}</span>
                          </label>
                        ))}
                      </div>
                      {formData.assigned_verticals.length > 0 && (
                        <p className="text-xs text-blue-600">{formData.assigned_verticals.length} vertical(s) selected</p>
                      )}
                    </div>
                  )}
                  <div className="space-y-2 col-span-2">
                    <label className="text-sm font-medium">Upload Limit (per month)</label>
                    <Input
                      type="number"
                      value={formData.upload_limit_monthly}
                      onChange={(e) => setFormData({ ...formData, upload_limit_monthly: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleAddUser}>Create User</Button>
                  <Button variant="outline" onClick={() => {
                    setShowAddForm(false);
                    setFormData({ name: '', email: '', password: '', role: 'creative', upload_limit_monthly: 100, assigned_verticals: [], additional_roles: [], pml_code: '' });
                    setError('');
                  }}>
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Signups */}
        {pendingUsers.length > 0 && (
          <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950 dark:border-orange-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-500 text-white text-sm font-bold">
                  {pendingUsers.length}
                </span>
                Pending Signups
              </CardTitle>
              <CardDescription>Review and approve new user registrations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-900 border border-orange-200 dark:border-orange-800 rounded-lg">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                        <Shield size={20} className="text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Role:</span>{' '}
                          <span className="font-medium capitalize">{user.role}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Status:</span>{' '}
                          <span className="text-orange-600 font-medium">Pending Approval</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleApproveUser(user.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check size={16} className="mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRejectingUserId(user.id)}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <X size={16} className="mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Users</CardTitle>
                <CardDescription>Manage user accounts and permissions</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-56 h-9"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {users.filter(u => {
                if (!userSearch) return true;
                const q = userSearch.toLowerCase();
                return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.role.toLowerCase().includes(q);
              }).map((user) => (
                <div key={user.id} className={`flex items-center justify-between p-4 border rounded-lg ${!user.is_active ? 'opacity-60 bg-muted/40' : ''}`}>
                  <div className="flex items-center gap-4 flex-1">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Shield size={20} className="text-primary" />
                    </div>
                    {editingId === user.id ? (
                      <div className="flex-1 grid grid-cols-4 gap-2">
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Name"
                        />
                        <select
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className="h-10 px-3 rounded-md border border-input bg-background"
                        >
                          <option value="creative">Creative</option>
                          <option value="buyer">Buyer</option>
                          <option value="admin">Admin</option>
                        </select>
                        <Input
                          type="number"
                          value={formData.upload_limit_monthly}
                          onChange={(e) => setFormData({ ...formData, upload_limit_monthly: parseInt(e.target.value) })}
                          placeholder="Upload limit"
                        />
                        <div className="col-span-4 mt-2">
                          <label className="text-sm font-medium text-muted-foreground mb-1 block">Additional Roles</label>
                          <div className="flex flex-wrap gap-2">
                            {['team_lead', 'assistant_team_lead', 'vertical_head', 'ceo', 'head_media_buying', 'creative_head', 'quality_check', 'variation_studio'].map(r => (
                              <label key={r} className="flex items-center gap-1 text-sm px-2 py-0.5 rounded border border-input hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.additional_roles?.includes(r)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData({ ...formData, additional_roles: [...(formData.additional_roles || []), r] });
                                    } else {
                                      setFormData({ ...formData, additional_roles: (formData.additional_roles || []).filter(x => x !== r) });
                                    }
                                  }}
                                />
                                <span className="capitalize">{r.replaceAll('_', ' ')}</span>
                              </label>
                            ))}
                          </div>
                          {formData.additional_roles?.length > 0 && (
                            <p className="text-xs text-blue-600 mt-1">{formData.additional_roles.length} additional role(s)</p>
                          )}
                        </div>
                        <div className="col-span-1">
                          <label className="text-xs text-muted-foreground mb-0.5 block">PML Code</label>
                          <input
                            type="text"
                            placeholder="PML80"
                            value={formData.pml_code}
                            onChange={(e) => setFormData({ ...formData, pml_code: e.target.value.replace(/\s+/g, '').toUpperCase() })}
                            className="w-full h-8 px-2 text-sm border border-input rounded-md bg-background font-mono"
                          />
                        </div>
                        {(['team_lead', 'assistant_team_lead', 'creative'].includes(formData.role) ||
                          formData.additional_roles?.some(r => ['vertical_head', 'team_lead', 'assistant_team_lead'].includes(r))) && (
                          <div className="col-span-4 mt-2">
                            <label className="text-sm font-medium text-muted-foreground mb-1 block">Assigned Verticals</label>
                            <input
                              type="text"
                              placeholder="Search verticals..."
                              value={verticalSearch}
                              onChange={(e) => setVerticalSearch(e.target.value)}
                              className="w-full h-8 px-2 mb-1 text-sm border border-input rounded-md bg-background"
                            />
                            <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
                              {verticalOptions.filter(v => !verticalSearch || v.toLowerCase().includes(verticalSearch.toLowerCase())).map((v) => (
                                <label key={v} className="flex items-center gap-1 text-sm px-2 py-0.5 rounded border border-input hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={formData.assigned_verticals?.includes(v)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFormData({ ...formData, assigned_verticals: [...(formData.assigned_verticals || []), v] });
                                      } else {
                                        setFormData({ ...formData, assigned_verticals: (formData.assigned_verticals || []).filter((x: string) => x !== v) });
                                      }
                                    }}
                                  />
                                  {v}
                                </label>
                              ))}
                            </div>
                            {formData.assigned_verticals?.length > 0 && (
                              <p className="text-xs text-blue-600 mt-1">{formData.assigned_verticals.length} selected</p>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex-1">
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    )}
                    {!editingId && (
                      <div className="flex gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Role:</span>{' '}
                          <span className="font-medium capitalize">{user.role}</span>
                          {(user as any).additional_roles?.length > 0 && (
                            <span className="text-xs text-blue-600 ml-1">
                              +{(user as any).additional_roles.map((r: string) => r.replaceAll('_', ' ')).join(', ')}
                            </span>
                          )}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Limit:</span>{' '}
                          <span className="font-medium">{user.upload_limit_monthly}/month</span>
                        </div>
                        {(user as any).pml_code && (
                          <div>
                            <span className="text-muted-foreground">PML:</span>{' '}
                            <span className="font-mono font-medium text-purple-700 dark:text-purple-300">{(user as any).pml_code}</span>
                          </div>
                        )}
                        <div>
                          <span className={user.is_active ? 'text-primary' : 'text-destructive'}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {editingId === user.id ? (
                      <>
                        <button
                          onClick={() => handleUpdateUser(user.id)}
                          className="p-2 hover:bg-accent rounded"
                        >
                          <Check size={18} className="text-primary" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(null);
                            setFormData({ name: '', email: '', password: '', role: 'creative', upload_limit_monthly: 100, assigned_verticals: [], additional_roles: [], pml_code: '' });
                          }}
                          className="p-2 hover:bg-accent rounded"
                        >
                          <X size={18} className="text-muted-foreground" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(user)}
                          className="p-2 hover:bg-accent rounded"
                          title="Edit user"
                        >
                          <Edit2 size={18} className="text-muted-foreground" />
                        </button>
                        <button
                          onClick={() => setResetPasswordUserId(user.id)}
                          className="p-2 hover:bg-accent rounded"
                          title="Reset password"
                        >
                          <Key size={18} className="text-muted-foreground" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(user)}
                          className={`p-2 hover:bg-accent rounded`}
                          title={user.is_active ? 'Deactivate user' : 'Reactivate user'}
                        >
                          {user.is_active
                            ? <UserX size={18} className="text-destructive" />
                            : <UserCheck size={18} className="text-green-600" />
                          }
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Password Reset Modal */}
        {resetPasswordUserId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md bg-white dark:bg-gray-900">
              <CardHeader>
                <CardTitle>Reset User Password</CardTitle>
                <CardDescription>
                  {generatedPassword
                    ? 'Password has been reset successfully'
                    : 'Enter your admin password to confirm password reset'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!generatedPassword ? (
                  <div className="space-y-4">
                    {error && (
                      <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-md">
                        {error}
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Your Admin Password</label>
                      <Input
                        type="password"
                        placeholder="Enter your password to confirm"
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        autoFocus
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">New Password for User</label>
                      <Input
                        type="text"
                        placeholder="Enter new password (min 8 characters)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Min 8 characters. You can copy this password to give to the user.
                      </p>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={closeResetModal}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => handleResetPassword(resetPasswordUserId)}
                        className="flex-1"
                        disabled={!adminPassword || !newPassword}
                      >
                        Reset Password
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-primary/10 rounded-lg space-y-2">
                      <p className="text-sm font-medium">New Password:</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-2 bg-background rounded text-sm font-mono">
                          {generatedPassword}
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(generatedPassword)}
                        >
                          <Copy size={16} />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Copy this password and provide it to the user.
                      </p>
                    </div>

                    <Button onClick={closeResetModal} className="w-full">
                      Done
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Rejection Reason Modal */}
        {rejectingUserId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md bg-white dark:bg-gray-900">
              <CardHeader>
                <CardTitle>Reject User Registration</CardTitle>
                <CardDescription>
                  Provide a reason for rejection (optional)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Reason for rejection</label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="e.g., Email domain not approved, Invalid role selection, etc."
                      className="w-full h-24 px-3 py-2 rounded-md border border-input bg-background text-sm resize-none"
                    />
                  </div>

                  {error && (
                    <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                      {error}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleRejectUser(rejectingUserId)}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      Confirm Rejection
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setRejectingUserId(null);
                        setRejectionReason('');
                        setError('');
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
